import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Webhook } from 'lucide-react';
import {
  createWebhook,
  deleteWebhook,
  listWebhooks,
  updateWebhook,
  WEBHOOK_EVENT_TYPES,
  type CreateWebhookInput,
  type WebhookEndpoint,
  type WebhookEventType,
} from '../api/webhooks';
import { getApiClient } from '../api/factory';
import { useAuthStore } from '../store/auth';
import { ApiError } from '../api/client';
import { useT } from '../i18n';
import { PageContainer, PageHeader } from '../components/Page';
import { EmptyState } from '../components/EmptyState';
import { SkeletonList } from '../components/Skeleton';

export function WebhooksPage() {
  const session = useAuthStore((s) => s.session);
  const isAdmin = session?.operator.role === 'admin';
  const queryClient = useQueryClient();
  const [lastSecret, setLastSecret] = useState<{ id: string; secret: string } | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const t = useT();

  const { data, isLoading, error } = useQuery({
    queryKey: ['webhooks'] as const,
    queryFn: () => listWebhooks(getApiClient()),
  });

  const create = useMutation({
    mutationFn: (input: CreateWebhookInput) => createWebhook(getApiClient(), input),
    onSuccess: (ep) => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      if (ep.secret) setLastSecret({ id: ep.id, secret: ep.secret });
      setShowCreate(false);
    },
  });

  return (
    <PageContainer>
      <PageHeader
        title={t('webhooks.title')}
        action={
          isAdmin && (
            <button
              type="button"
              onClick={() => setShowCreate((v) => !v)}
              className="rounded-md bg-accent-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-accent-700"
            >
              {t('webhooks.create')}
            </button>
          )
        }
      />

      {isAdmin && showCreate && (
        <WebhookForm
          loading={create.isPending}
          error={create.error instanceof ApiError ? create.error.message : null}
          onSubmit={(input) => create.mutate(input)}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {lastSecret && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-4">
          <div className="mb-2 text-sm font-medium text-amber-900">
            {t('webhooks.secretBanner', { id: lastSecret.id.slice(0, 8) })}
          </div>
          <code className="block break-all rounded bg-white p-2 font-mono text-xs">
            {lastSecret.secret}
          </code>
          <button
            type="button"
            onClick={() => setLastSecret(null)}
            className="mt-2 text-xs text-amber-800 hover:text-amber-900"
          >
            {t('common.hide')}
          </button>
        </div>
      )}

      {isLoading && <SkeletonList ariaLabel={t('common.loading')} />}
      {error instanceof ApiError && (
        <p role="alert" className="text-sm text-red-600">
          {error.message}
        </p>
      )}

      {data && data.length === 0 && (
        <EmptyState
          icon={Webhook}
          title={t('webhooks.empty')}
          {...(isAdmin
            ? {
                actionLabel: t('webhooks.create'),
                onAction: () => setShowCreate(true),
              }
            : {})}
        />
      )}

      {data && data.length > 0 && (
        <ul className="space-y-3">
          {data.map((ep) => (
            <WebhookRow key={ep.id} ep={ep} canEdit={isAdmin} />
          ))}
        </ul>
      )}
    </PageContainer>
  );
}

function WebhookForm({
  onSubmit,
  loading,
  error,
  onCancel,
}: {
  onSubmit: (input: CreateWebhookInput) => void;
  loading: boolean;
  error: string | null;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<WebhookEventType[]>(['message.new']);
  const t = useT();

  function toggle(e: WebhookEventType) {
    setEvents((prev) => (prev.includes(e) ? prev.filter((x) => x !== e) : [...prev, e]));
  }

  function handle(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (name.trim() === '' || url.trim() === '' || events.length === 0) return;
    onSubmit({ name: name.trim(), url: url.trim(), events });
    setName('');
    setUrl('');
    setEvents(['message.new']);
  }

  return (
    <form
      onSubmit={handle}
      className="mb-4 space-y-3 rounded-md border border-slate-200 bg-white p-4 shadow-card"
    >
      <div className="flex gap-3">
        <label className="flex-1">
          <span className="mb-1 block text-xs text-slate-500">{t('webhooks.name')}</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100"
            placeholder="CRM webhook"
          />
        </label>
        <label className="flex-1">
          <span className="mb-1 block text-xs text-slate-500">{t('webhooks.url')}</span>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100"
            placeholder="https://crm.example.com/anexica"
          />
        </label>
      </div>
      <fieldset>
        <legend className="mb-1 text-xs text-slate-500">{t('webhooks.events')}</legend>
        <div className="flex flex-wrap gap-3">
          {WEBHOOK_EVENT_TYPES.map((e) => (
            <label
              key={e}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs"
            >
              <input
                type="checkbox"
                checked={events.includes(e)}
                onChange={() => toggle(e)}
                className="accent-accent-600"
              />
              <code className="text-slate-700">{e}</code>
            </label>
          ))}
        </div>
      </fieldset>
      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={loading || name.trim() === '' || url.trim() === '' || events.length === 0}
          className="rounded-md bg-accent-600 px-3.5 py-1.5 text-sm font-medium text-white hover:bg-accent-700 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          {t('webhooks.create')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md px-3 py-1.5 text-sm text-slate-500 hover:text-slate-900"
        >
          {t('common.cancel')}
        </button>
        {error && <span className="text-xs text-red-600">{error}</span>}
      </div>
    </form>
  );
}

function WebhookRow({ ep, canEdit }: { ep: WebhookEndpoint; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const t = useT();
  const toggle = useMutation({
    mutationFn: () =>
      updateWebhook(getApiClient(), ep.id, {
        status: ep.status === 'active' ? 'paused' : 'active',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['webhooks'] }),
  });
  const remove = useMutation({
    mutationFn: () => deleteWebhook(getApiClient(), ep.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['webhooks'] }),
  });

  return (
    <li className="rounded-md border border-slate-200 bg-white p-4 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-slate-900">{ep.name}</div>
          <div className="break-all text-xs text-slate-500">{ep.url}</div>
          <div className="mt-2 flex flex-wrap gap-1">
            {ep.events.map((e) => (
              <code
                key={e}
                className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-700"
              >
                {e}
              </code>
            ))}
          </div>
        </div>
        <StatusPill active={ep.status === 'active'} label={ep.status} />
      </div>
      {canEdit && (
        <div className="mt-3 flex gap-3 text-xs">
          <button
            type="button"
            onClick={() => toggle.mutate()}
            className="text-slate-600 hover:text-slate-900"
          >
            {ep.status === 'active' ? t('webhooks.pause') : t('webhooks.resume')}
          </button>
          <button
            type="button"
            onClick={() => {
              if (confirm(t('webhooks.confirmDelete', { name: ep.name }))) remove.mutate();
            }}
            className="text-red-600 hover:text-red-700"
          >
            {t('webhooks.delete')}
          </button>
        </div>
      )}
    </li>
  );
}

function StatusPill({ active, label }: { active: boolean; label: string }) {
  return (
    <span
      className={
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ' +
        (active ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700')
      }
    >
      <span
        className={
          'mr-1.5 h-1.5 w-1.5 rounded-full ' +
          (active ? 'bg-emerald-500' : 'bg-amber-500')
        }
        aria-hidden="true"
      />
      {label}
    </span>
  );
}
